import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import type { JobListItem, ProfileListItem } from '../../types/api'
import Badge from '../../components/ui/Badge'
import Modal from '../../components/ui/Modal'
import Button from '../../components/ui/Button'

function ProfileSelectionModal({
  isOpen,
  onClose,
  jobId,
}: {
  isOpen: boolean
  onClose: () => void
  jobId: string
}) {
  const navigate = useNavigate()
  const [profiles, setProfiles] = useState<ProfileListItem[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    api.profiles.list()
      .then((res) => setProfiles(res.data))
      .finally(() => setLoading(false))
  }, [isOpen])

  const select = (profileId: string) => {
    navigate(`/candidate/profile/${profileId}`, { state: { jobId } })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="SELECT YOUR PROFILE">
      {loading ? (
        <p className="text-text-muted text-sm">Loading...</p>
      ) : (
        <div className="space-y-2">
          {profiles.map((p) => (
            <button
              key={p.id}
              onClick={() => select(p.id)}
              className="w-full text-left bg-surface-low border border-border-subtle p-3 hover:border-primary transition-colors group"
            >
              <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{p.name}</p>
              {p.skills.length > 0 && (
                <p className="text-xs text-text-muted mt-1">{p.skills.slice(0, 4).join(', ')}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </Modal>
  )
}

export default function CandidateJobs() {
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedJobId, setSelectedJobId] = useState('')
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    api.jobs.list()
      .then((res) => {
        setJobs(res.data.filter((j) => j.type !== 'Exclusive' && j.status === 'Open'))
      })
      .finally(() => setLoading(false))
  }, [])

  const openModal = (jobId: string) => {
    setSelectedJobId(jobId)
    setShowModal(true)
  }

  if (loading) return <p className="text-text-muted text-sm">Loading jobs...</p>

  return (
    <>
      <div className="space-y-6">
        <h1 className="text-2xl font-heading tracking-tactical">AVAILABLE JOBS</h1>
        {jobs.length === 0 ? (
          <p className="text-text-muted text-sm">No open jobs available right now.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-surface-low border border-border-subtle p-5 space-y-3"
              >
                <div>
                  <p className="font-medium text-foreground">{job.title}</p>
                  <p className="text-xs text-text-muted mt-0.5">{job.employer}</p>
                </div>
                {job.required_topics.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {job.required_topics.map((t) => <Badge key={t} variant="required">{t}</Badge>)}
                  </div>
                )}
                <div className="pt-1">
                  <Button
                    variant="secondary"
                    onClick={() => openModal(job.id)}
                    className="text-xs"
                  >
                    FIND MY FIT →
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProfileSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        jobId={selectedJobId}
      />
    </>
  )
}
